/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { Fragment } from 'react';
import { Button } from 'reactstrap';
import SimpleTooltip from '../simple-tooltip';
import { Breadcrumb } from '../../types/surveyScope';
import { ViewMap, ViewValues } from '../survey-overview-sidebar/viewOptions';
import SortButton from './SortButton';
import { SortType } from '../../utils/sort';
import { ViewQuestionsDropdown } from '../survey-overview-sidebar/partials/ViewQuestionsDropdown';
import { CardGridViewModeButtons } from '../survey-overview-sidebar/partials/CardGridViewModeButtons';
import './styles.scss';
import { SurveySettings } from '../../types/survey';
import { HandleChangeSettingsType } from '../survey-container/SurveyContainer';
import { QUESTION } from '../../constants/terminology';

interface SurveySettingsToolbarProps {
  viewQuestionsLabel?: string;
  view: ViewValues;
  breadcrumbs: Breadcrumb[],
  handleDrillback: (breadcrumbs: Breadcrumb[]) => void,
  handleSort: (code: SortType) => void,
  children?: JSX.Element | JSX.Element[];
  currentSort: SortType,
  filterText?: string,
  setFilterText?: (text: string) => void;
  questionCount?: number;
  sidebarSettings: SurveySettings,
  handleChangeSettings: HandleChangeSettingsType,
  hideViewQuestionsDropdown: boolean;
}

const getRootTitle = (key: ViewValues) => ViewMap[key]?.label ?? 'Assigned Metrics';

const renderBreadcrumb = (text: string, onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void, isLeaf: boolean) => {
  return <>
    <div><i className='fa fa-caret-right mx-2' /></div>
    <div className='survey-settings-breadcrumb'>
      <SimpleTooltip text={text} component={
        <Button color='link' onClick={onClick} className={isLeaf ? 'leaf' : ''}>{text}</Button>
      } />
    </div>
  </>
};

export default function SurveySettingsToolbar(props: SurveySettingsToolbarProps) {
  const {
    view,
    breadcrumbs,
    handleDrillback,
    handleSort,
    currentSort,
    viewQuestionsLabel = `View ${QUESTION.PLURAL} by`,
    sidebarSettings,
    handleChangeSettings,
    hideViewQuestionsDropdown
  } = props;

  const handleBreadcrumbClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
    e.preventDefault();
    handleDrillback(breadcrumbs.slice(0, index));
  };
  const currentView = breadcrumbs[breadcrumbs.length - 1];
  const isQuestionList = currentView && [ViewValues.QuestionList, ViewValues.StandardsQuestionList].includes(currentView.cardGroup);
  return (
    <div className='survey-settings-toolbar-container'>
      <div className='survey-settings-toolbar d-flex justify-content-end align-items-center'>
        {hideViewQuestionsDropdown ? null : (
          <>
            <div className='mr-2'>
              {viewQuestionsLabel}
            </div>
            <div className='mr-auto'>
              <ViewQuestionsDropdown sidebarSettings={sidebarSettings} handleChangeSettings={handleChangeSettings} />
            </div>
          </>
        )}
        {props.children}
        <div className='ms-2'>
          <CardGridViewModeButtons />
        </div>
        <span className='ms-2'>
          {isQuestionList ? null : <SortButton currentSort={currentSort} handleSort={handleSort} />}
        </span>
      </div>
      <div className='mt-1 survey-settings-toolbar-breadcrumbs d-flex'>
        {breadcrumbs.length > 0 ? renderBreadcrumb(getRootTitle(view), (e) => handleBreadcrumbClick(e, 0), breadcrumbs.length === 0) : null}
        {breadcrumbs && breadcrumbs.map((breadcrumb, i) => (
          <Fragment key={`breadcrumb-${breadcrumb.cardGroup}-${i}`}>
            {renderBreadcrumb(breadcrumb.title, (e) => handleBreadcrumbClick(e, i + 1), (i + 1) === breadcrumbs.length)}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
